/* eslint-disable react-hooks/exhaustive-deps */
import { Close, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
  Slider
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Brush,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ComposedChart,
  Label,
  Legend,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import {
  setEndtimeRDT,
  setMaxSliderValueKP_GE,
  setStarttimeRDT,
  setTopGenesSlice,
  setTrendsGraphDrugClass,
  setTrendsGraphView,
} from '../../../../stores/slices/graphSlice';
import { colorForMarkers, colorForMarkersCVD, hoverColor } from '../../../../util/colorHelper';
import { drugClassesNG, drugClassesST, drugsECOLI, drugsSA, drugsSP, markersDrugsKP, markersDrugsSH, ciproAcronyms } from '../../../../util/drugs';
import { getRange } from '../../../../util/helpers';
import { isTouchDevice } from '../../../../util/isTouchDevice';
import { SelectCountry } from '../../SelectCountry';
import { SliderSizes } from '../../Slider';
import { useStyles } from './MarkerTrendsGraphMUI';
import { PlottingOptionsHeader } from '../../Shared/PlottingOptionsHeader';
import { useTranslation } from 'react-i18next';

const dataViewOptions = [
  { label: 'Number of genomes', value: 'number' },
  { label: 'Percentage per year', value: 'percentage' },
];

export const MarkerTrendsGraph = ({ showFilter, setShowFilter }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [currentTooltip, setCurrentTooltip] = useState(null);
  const [plotChart, setPlotChart] = useState(() => {});
  const [logScale, setLogScale] = useState(false);
  const [yAxisSliderValue, setYAxisSliderValue] = useState([0, 100]);

  // const [colourPattern, setColourPattern] = useState(false);
  const [lineStyle, setLineStyle] = useState('');


  const getLineStyleForDrug = (genName) => {
   const lineStyles = ["Normal", "Thick"];

    const drugIndex = [...(topGenesSlice || []).filter(x => x !== 'None'), 'Other Genes', 'None'].indexOf(genName);
    return drugIndex !== -1 ? lineStyles[drugIndex % lineStyles.length] : lineStyles[0];
  };
  
  const dispatch = useAppDispatch();
  const organism = useAppSelector(state => state.dashboard.organism);
  const canGetData = useAppSelector(state => state.dashboard.canGetData);
  const genotypesForFilter = useAppSelector(state => state.dashboard.genotypesForFilter);
  const timeInitial = useAppSelector(state => state.dashboard.timeInitial);
  const timeFinal = useAppSelector(state => state.dashboard.timeFinal);
  const genotypesAndDrugsYearData = useAppSelector(state => state.graph.genotypesAndDrugsYearData);
  const trendsGraphView = useAppSelector(state => state.graph.trendsGraphView);
  const trendsGraphDrugClass = useAppSelector(state => state.graph.trendsGraphDrugClass);
  const resetBool = useAppSelector(state => state.graph.resetBool);
  const currentSliderValueKP_GE = useAppSelector(state => state.graph.currentSliderValueKP_GE);
  const topGenesSlice = useAppSelector(state => state.graph.topGenesSlice);
  const canFilterData = useAppSelector(state => state.dashboard.canFilterData);
  const colourPattern = useAppSelector((state) => state.dashboard.colourPattern);
  

  useEffect(() => {
    //dispatch(setResetBool(true));
    setCurrentTooltip(null);
  }, [genotypesAndDrugsYearData]);

  useEffect(() => {
    setCurrentTooltip(null);
  }, [currentSliderValueKP_GE]);

  const dataViewOptionsGenomes = [
    { label: 'Number of Genomes', value: 'number' },
    { label: 'Percentage of Genomes', value: 'percentage' },
  ];

  // For ecoli-family organisms the marker view collapses
  // 'Ciprofloxacin NS' + 'Ciprofloxacin R' into a single 'Ciprofloxacin'
  // (≥1 marker in Quinolone). Prevalence views (AMR Trends, main map legend,
  // BubbleGeographicGraph resistance mode) keep the split — they consume
  // different drug lists.
  const collapseCiproForMarkers = list =>
    list.filter(d => d !== 'Ciprofloxacin NS' && d !== 'Ciprofloxacin R');

  function getDrugClasses() {
    if (organism === 'none') {
      return [];
    }
    if (organism === 'kpneumo') {
      return markersDrugsKP;
    }
    if (organism === 'styphi') {
      return drugClassesST.filter(x => x !== 'Trimethoprim-sulfamethoxazole' && x !== 'XDR' && x !== 'MDR');
    }
    if (organism === 'shige' || organism === 'decoli' || organism === 'ecoli') {
      return collapseCiproForMarkers(markersDrugsSH);
    }
    if (organism === 'ngono') {
      return drugClassesNG;
    }
    if (organism === 'saureus') {
      return drugsSA.filter(x => x !== 'Pansusceptible');
    }
    if (organism === 'strepneumo') {
      return drugsSP.filter(x => x !== 'Pansusceptible');
    }

    // Fallback covers senterica and sentericaints.
    return collapseCiproForMarkers(drugsECOLI).filter(item => item !== 'Pansusceptible');
  }

 function getDomain(max = null) {
   if (trendsGraphView === 'number') {
     return logScale? [yAxisSliderValue[0], yAxisSliderValue[1] ?? max ?? 'dataMax'] : trendsGraphView === 'number' ? ['dataMin', max ?? 'dataMax'] : [0, 100];
   }
   // percentage view -> zoomable 0–100
   return [yAxisSliderValue[0], yAxisSliderValue[1]];
 }

  const yearsData = useMemo(() => {
    const yearsArray = Array.isArray(genotypesAndDrugsYearData[trendsGraphDrugClass])
      ? genotypesAndDrugsYearData[trendsGraphDrugClass]
      : [];
    return (
      yearsArray.map(x => {
        const object = {};

        Object.keys(x).forEach(key => {
          if (!genotypesForFilter.includes(key)) {
            object[key] = x[key];
          }
        });

        return object;
      }) || []
    );
  }, [genotypesAndDrugsYearData, trendsGraphDrugClass, genotypesForFilter]);

  // Step 1: Pure calculation of counts and sorted keys.
  const processedData = useMemo(() => {
    const genes = {};

    yearsData?.forEach(year => {
      Object.keys(year).forEach(key => {
        if (['name', 'totalCount', 'resistantCount'].includes(key)) {
          return;
        }

        if (key in genes) {
          genes[key] += year[key];
        } else {
          genes[key] = year[key];
        }
      });
    });

    const sortedGeneKeys = Object.keys(genes).sort((a, b) => genes[b] - genes[a]);

    return { genes, sortedGeneKeys };
  }, [yearsData]);

  // Step 2: Perform side effects (dispatching to Redux) after calculation.
  useEffect(() => {
    if (!processedData) return;

    const { genes, sortedGeneKeys } = processedData;
    // This is a side effect and belongs in useEffect
    dispatch(setMaxSliderValueKP_GE(Object.keys(genes).length));

    const topGE = sortedGeneKeys.slice(0, currentSliderValueKP_GE);

    // These are also side effects
    dispatch(setTopGenesSlice(topGE));
  }, [processedData, currentSliderValueKP_GE, dispatch]);

  // Step 3: Calculate the final data for the chart based on Redux state.
  const slicedData = useMemo(() => {
    const slicedDataArray = [];

    yearsData?.forEach(year => {
      // N≥10 rule: skip years with insufficient genomes. The useEffect below
      // re-inserts them as "Insufficient data" placeholders so the x-axis stays
      // continuous but nothing plots at low-count years.
      if ((year.totalCount ?? 0) < 10) {
        return;
      }
      const value = {
        name: year.name,
        totalCount: year.totalCount,
        resistantCount: year.resistantCount,
        // 'Other Genes': 0,
      };

      const keys = Object.keys(year).filter(key => !['name', 'totalCount', 'resistantCount'].includes(key));
      keys.forEach(key => {
        if (topGenesSlice.includes(key)) {
          value[key] = year[key];
          return;
        }

        // value['Other Genes'] += year[key];
      });

      processedData.sortedGeneKeys
        .filter(key => !keys.includes(key))
        .forEach(key => {
          value[key] = 0;
        });

      slicedDataArray.push(value);
    });

    return slicedDataArray;
  }, [yearsData, topGenesSlice]);

  const maxDataValue = useMemo(() => {
    if (!slicedData || slicedData.length === 0) return 100;
    let max = 0;
    slicedData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!['name', 'totalCount', 'resistantCount'].includes(key)) {
          if (item[key] > max) max = item[key];
        }
      });
    });
    return max;
  }, [slicedData]);

  function getData() {
    if (trendsGraphView === 'number') {
      return slicedData;
    }

    const exclusions = ['name', 'totalCount', 'resistantCount'];
    let percentageData = structuredClone(slicedData ?? []);
    percentageData = percentageData.map(item => {
      const keys = Object.keys(item).filter(x => !exclusions.includes(x));

      keys.forEach(key => {
        item[key] = Number(((item[key] / item.totalCount) * 100).toFixed(2));
      });

      return item;
    });

    return percentageData;
  }

  function handleChangeDataView(event) {
    dispatch(setTrendsGraphView(event.target.value));
  }

    function handleSliderChangeDataView(event) {
      setYAxisSliderValue(event.target.value);
    }

  function handleChangeDrugClass(event) {
    setCurrentTooltip(null);
    dispatch(setTrendsGraphDrugClass(event.target.value));
  }

  function handleSwitchScale(event) {
    setCurrentTooltip(null);
    setLogScale(event.target.checked);
  }

  function handleClickChart(event) {
    const data = slicedData.find(item => item.name === event?.activeLabel);

    if (data && data.totalCount >= 10) {
      const currentData = structuredClone(data);
      const value = {
        name: currentData.name,
        count: currentData.totalCount,
        genes: [],
      };

      delete currentData.name;
      delete currentData.totalCount;
      delete currentData.resistantCount;

      Object.keys(currentData).forEach(key => {
        const count = currentData[key];

        // if (count === 0) {
        //   return;
        // }

        const item = {
          label: key,
          count,
          percentage: Number(((count / value.count) * 100).toFixed(2)),
          color: event.activePayload.find(x => x.name === key)?.color,
        };

        value.genes.push(item);
        value.genes.sort((a, b) => {
          if (a.label === 'None') return 1;
          if (b.label === 'None') return -1;
          return a.label.localeCompare(b.label);
        });
      });

      setCurrentTooltip(value);
    } else if (event?.activeLabel === undefined || event?.activeLabel === null) {
      setCurrentTooltip(null);
    } else {
      setCurrentTooltip({
        name: event?.activeLabel,
        count: 'ID',
        genes: [],
      });
    }
    //dispatch(setResetBool(false));
  }

  useEffect(() => {
    if (resetBool) {
      setCurrentTooltip(null);
    }
  });

  useEffect(() => {
    if (slicedData.length > 0) {
      // Dispatch initial values based on the default range (full range)
      const startValue = slicedData[0]?.name; // First value in the data
      const endValue = slicedData[slicedData.length - 1]?.name; // Last value in the data
      dispatch(setStarttimeRDT('2000'));
      dispatch(setEndtimeRDT(endValue));
    }
  }, [slicedData, dispatch]);

  useEffect(() => {
    if (canGetData) {
      // Get data
      const data = getData();

      let allYears = [];
      if (data.length > 0) {
        // Add missing years between the select time to show continuous scale
        allYears = getRange(Number(data[0].name), Number(data[data.length - 1].name))?.map(String);
        const years = data.map(x => x.name.toString());

        allYears.forEach(year => {
          if (!years.includes(year)) {
            const index = data.findIndex(d => d.name === year);
            if (index !== -1) {
              data.splice(index, 1); // Remove existing entry in-place
            }
            data.push({
              name: year,
              'Insufficient data': trendsGraphView === 'number' ? 0 : 100,
            });
          }
        });

        data.sort((a, b) => a.name.toString().localeCompare(b.name).toString());
      }

      setPlotChart(() => {
        return (
          <ResponsiveContainer width="100%">
            <ComposedChart data={data} cursor={isTouchDevice() ? 'default' : 'pointer'} onClick={handleClickChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                tickCount={20}
                allowDecimals={false}
                padding={{ left: 20, right: 20 }}
                dataKey="name"
                domain={(slicedData ?? []).length > 0 ? ['dataMin', 'dataMax'] : undefined}
                interval={'preserveStartEnd'}
                tick={{ fontSize: 14 }}
              />
              <YAxis
                tickCount={logScale ? 8 : 6}
                padding={{ top: 20, bottom: 20 }}
                allowDecimals={false}
                domain={getDomain()}
                allowDataOverflow={true}
                scale={logScale ? 'linear' : undefined}
              >
                <Label angle={-90} position="insideLeft" className={classes.graphLabel}>
                  {dataViewOptionsGenomes.find(option => option.value === trendsGraphView).label}
                </Label>
              </YAxis>
              {(slicedData ?? []).length > 0 && (
                <Brush
                  dataKey="name"
                  height={20}
                  stroke={'rgb(31, 187, 211)'}
                  startIndex={allYears.findIndex(x => x === '2000') || 0}
                  onChange={brushRange => {
                    // Ignore clicks that collapse the brush to a single year.
                    if (brushRange == null || brushRange.startIndex === brushRange.endIndex) return;
                    const startYear = data[brushRange.startIndex]?.name;
                    const endYear = data[brushRange.endIndex]?.name;
                    if (!startYear || !endYear) return;
                    dispatch(setStarttimeRDT(startYear));
                    dispatch(setEndtimeRDT(endYear));
                  }}
                />
              )}

              {organism !== 'none' && (
                <Legend
                  content={props => {
                    const { payload } = props;

                    return (
                      <div className={classes.legendWrapper}>
                        {payload.map((entry, index) => {
                          const { dataKey, color } = entry;

                          if (dataKey.includes('Other Genes')) {
                            const hasData = data.some(d => {
                              const value = d[dataKey];

                              return value !== undefined && value !== null && value !== 0;
                            });

                            if (!hasData) {
                              return null;
                            }
                          }

                          return (
                            <React.Fragment key={`trends-legend-${index}`}>
                              <div className={classes.legendItemWrapper}>
                                {!colourPattern ? <Box className={classes.colorCircle} style={{ backgroundColor: color }} />
                                :<svg width={16} height={16}>
                                  <circle
                                    cx="8"
                                    cy="8"
                                    r="2"
                                    fill="none"
                                    stroke={color}
                                    strokeWidth={index%2 == 0 ? 5 : 7}
                                    strokeDasharray={getLineStyleForDrug(dataKey)}
                                  />
                                </svg>}
                                <Typography variant="caption">{dataKey}</Typography>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  }}
                />
              )}

              <ChartTooltip
                cursor={{ fill: hoverColor }}
                content={({ payload, active, label }) => {
                  if (payload !== null && active) {
                    return <div className={classes.chartTooltipLabel}>{label}</div>;
                  }
                  return null;
                }}
              />

              {[...(topGenesSlice || []).filter(x => x !== 'None'), 'Other Genes', 'None'].map((option, index) => {
                let fillColor = '#F5F4F6';
                let lineStyle = '';

                if (option === 'Other Genes') {
                  fillColor = '#F5F4F6';
                } else if (option === 'None') {
                  fillColor = '#B9B9B9';
                } else {
                  if (!colourPattern){
                    fillColor = colorForMarkers(index);
                  }else{
                    fillColor =  colorForMarkersCVD [index]
                    lineStyle = getLineStyleForDrug(option);
                  }
                }

                return (
                  <Line
                    key={`trends-line-${index}`}
                    dataKey={option}
                    strokeWidth={!colourPattern ? 2 : index%2 == 0 ? 2 : 3}
                    stroke={fillColor}
                    strokeDasharray={!colourPattern ? 'solid': lineStyle}
                    connectNulls
                    type="monotone"
                    activeDot={timeInitial === timeFinal ? true : false}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsData, trendsGraphView, trendsGraphDrugClass, currentSliderValueKP_GE, slicedData, topGenesSlice, logScale, colourPattern, lineStyle, yAxisSliderValue]);

  return (
    <CardContent className={classes.markerTrendsGraph}>

      <div className={classes.graphWrapper}>
        <div className={classes.graph} id="RDT">
          {plotChart}
        </div>
        <div className={classes.rightSide}>
          <SliderSizes value={'KP_GE'} style={{ width: '100%' }} />
          <FormGroup className={classes.formGroup}>
            <FormControlLabel
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                 {t('common.changeYAxisLimits')} <Typography variant="caption">
                  </Typography>
                  <Tooltip title={t('common.dataZoomTooltip')} placement="top">
                    <InfoOutlined color="action" fontSize="small" />
                  </Tooltip>
                </Box>
              }
              control={<Switch checked={logScale} onChange={handleSwitchScale} />}
            />
          </FormGroup>
          {logScale ? (
            <>
              <Typography variant="caption">{t('common.adjustYRange')}</Typography>
              <Slider
                value={yAxisSliderValue}
                onChange={handleSliderChangeDataView}
                step={1}
                min={0}
                max={trendsGraphView === 'number'? maxDataValue : 100}
                valueLabelDisplay="auto"
              />
            </>
          ) : null}
          <div className={classes.tooltipWrapper}>
            {currentTooltip ? (
              <div className={classes.tooltip}>
                <div className={classes.tooltipTitle}>
                  <Typography variant="h5" fontWeight="600">
                    {currentTooltip.name}
                  </Typography>
                  {currentTooltip.count !== 'ID' && (
                    <Typography variant="subtitle1">{'N = ' + currentTooltip.count}</Typography>
                  )}
                </div>
                {currentTooltip.count === 'ID' ? (
                  <div className={classes.insufficientData}>Insufficient data</div>
                ) : (
                  <div className={classes.tooltipContent}>
                    {currentTooltip.genes.map((item, index) => {
                      return (
                        <div key={`tooltip-content-${index}`} className={classes.tooltipItemWrapper}>
                        {(() => {
                          // Find the corresponding drug name for this tooltip item
                            const drugKey = Object.keys(ciproAcronyms).find(key => ciproAcronyms[key] === item.label) || item.label;
                            const lineStyle = getLineStyleForDrug(drugKey);
                            
                            return colourPattern && lineStyle !== 'Normal' ? (
                              <div
                                className={classes.tooltipItemBox}
                                style={{
                                  backgroundColor: item.color,
                                }}
                              />
                            ) : (
                              <div
                                className={classes.tooltipItemBoxSmall}
                                style={{
                                  backgroundColor: item.color,
                                }}
                              />
                            );
                          })()} 

                          <div className={classes.tooltipItemStats}>
                            <Typography variant="body2" fontWeight="500">
                              {item.label}
                            </Typography>
                            <Typography variant="caption" noWrap>{`N = ${item.count}`}</Typography>
                            <Typography fontSize="10px">{`${item.percentage}%`}</Typography>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className={classes.noYearSelected}>{t('common.noYearSelected')}</div>
            )}
          </div>
        </div>
      </div>
      {showFilter && !canFilterData && (
        <Box className={classes.floatingFilter}>
          <Card elevation={3}>
            <CardContent>
              <PlottingOptionsHeader onClose={() => setShowFilter(false)} className={classes.titleWrapper} />
              <div className={classes.selectsWrapper}>
                <SelectCountry />
                <div className={classes.selectPreWrapper}>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">Select drug class</Typography>
                    </div>
                    <Select
                      value={trendsGraphDrugClass}
                      onChange={handleChangeDrugClass}
                      inputProps={{ className: classes.selectInput }}
                      MenuProps={{ classes: { list: classes.selectMenu } }}
                      disabled={organism === 'none'}
                    >
                      {getDrugClasses()
                        ?.filter(x => !['MDR', 'XDR'].includes(x))
                        .map((option, index) => {
                          return (
                            <MenuItem key={index + 'trends-drug-classes'} value={option}>
                              {option}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </div>
                </div>
                <div className={classes.selectPreWrapper}>
                  <div className={classes.selectWrapper}>
                    <div className={classes.labelWrapper}>
                      <Typography variant="caption">Data view</Typography>
                    </div>
                    <Select
                      value={trendsGraphView}
                      onChange={handleChangeDataView}
                      inputProps={{ className: classes.selectInput }}
                      MenuProps={{ classes: { list: classes.selectMenu } }}
                      disabled={organism === 'none'}
                    >
                      {dataViewOptions.map((option, index) => {
                        return (
                          <MenuItem key={index + 'trends-dataview'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Box>
      )}
    </CardContent>
  );
};
